import React, { useMemo } from 'react';

import { openBlob } from '@apis/common';
import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import { dashboardDeliveryRequestLogApis } from '@apis/next/deliveryRequestLogApis';
import { dashboardPartnerApis } from '@apis/next/partnerApis';
import { PartnerLink } from '@components/admin/widgets/PartnerLink';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogTitle,
} from '@components/Dialog';
import { DebugForm } from '@components/NextForms/DebugForm';
import { IndexedFormContainer, IndexedLabel } from '@components/NextForms/IndexedForm';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { resolveDeliveryRequestBatchSubmitErrors } from '@data/deliveryRequest';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  DeliveryRequestBatchSubmitAction,
  DeliveryRequestListItem,
  DeliveryRequestBatchSubmitForm,
  DeliveryRequestBatchYokomochiSubmitForm,
} from '@models/next/DeliveryRequest';
import { Partner } from '@models/next/Partner';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    title: () => ({
      BATCH_MAIL_SUBMIT: '配車依頼書を送信する',
      BATCH_CSV_SUBMIT: '貨物の送り状用CSVを出力する',
      BATCH_YOKOMOCHI_SUBMIT: '横持配車依頼書を送信する',
    }),
    submit: () => ({
      BATCH_MAIL_SUBMIT: '送信',
      BATCH_CSV_SUBMIT: '出力',
      BATCH_YOKOMOCHI_SUBMIT: '送信',
    }),
    cannotBatchSubmitDeliveryRequest: `下記の原因で処理できません：`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '2.5rem 2rem',
    flexShrink: 1,
    minHeight: 0,
    maxHeight: 550,
  },
}));

const DeliveryRequestBatchSubmitDialogContent: React.FC<
  DeliveryRequestBatchSubmitDialogProps & { partners: Partner[] }
> = (props) => {
  const { action, deliveryRequests, partners, onClose } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const queryClient = useQueryClient();

  // hooks.useMutation has a problem with typing in `params`
  const submitCsvQuery = useMutation(() =>
    dashboardDeliveryRequestApis.client.get('/dashboard/delivery-request/submit-csv', {
      queries: { id: deliveryRequests.map((d) => d.id) },
    }),
  );

  const putDeliveryRequest = dashboardDeliveryRequestApis.usePut(
    '/dashboard/delivery-request',
    {
      queries: {
        id: deliveryRequests.map((d) => d.id),
        action,
      },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardDeliveryRequestApis.getKeyByPath('get', '/dashboard/delivery-request'),
        );

        queryClient.invalidateQueries(
          dashboardDeliveryRequestApis.getKeyByPath('get', '/dashboard/delivery-request/:id'),
        );

        queryClient.invalidateQueries(
          dashboardDeliveryRequestLogApis.getKeyByPath(
            'get',
            '/dashboard/delivery-request-log/:id',
          ),
        );
      },
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [putDeliveryRequest],
  });

  const form = useForm(DeliveryRequestBatchSubmitForm);

  const yokomochiForm = useForm(DeliveryRequestBatchYokomochiSubmitForm);

  return (
    <>
      <ActionableDialogTitle>{strings.title()[action]}</ActionableDialogTitle>

      <form
        {...(action === 'BATCH_YOKOMOCHI_SUBMIT' ? yokomochiForm : form).bindForm({
          async onSubmit(values) {
            await putDeliveryRequest.mutateAsync({
              ...values,
            });

            switch (action) {
              case 'BATCH_CSV_SUBMIT':
                const blob = await submitCsvQuery.mutateAsync();

                openBlob(blob, '配車CSV.csv', 'application/csv', true);
                break;
              default:
                break;
            }

            onClose();
          },
        })}
      >
        <div className={classes.dialogContent}>
          <div>{renderAlertsAndSpinnerModal()}</div>

          <IndexedFormContainer size="md">
            {action !== 'BATCH_YOKOMOCHI_SUBMIT' &&
              (() => {
                const { fields } = form.root;

                return (
                  <>
                    <IndexedLabel field={fields.partner_id} />
                    <div>
                      <TextInput
                        field={fields.partner_id}
                        inputOptions={{
                          type: 'choose',
                          options: partners.map((item) => ({
                            label: item.name_en_short,
                            value: item.id,
                            labelIcon: <PartnerLink partnerId={item.id} />,
                          })),
                        }}
                      />
                    </div>
                  </>
                );
              })()}

            {action === 'BATCH_YOKOMOCHI_SUBMIT' &&
              (() => {
                const { fields } = yokomochiForm.root;

                return (
                  <>
                    <IndexedLabel field={fields.yokomochi_partner_id} />
                    <div>
                      <TextInput
                        field={fields.yokomochi_partner_id}
                        inputOptions={{
                          type: 'choose',
                          options: partners.map((item) => ({
                            label: item.name_en_short,
                            value: item.id,
                          })),
                        }}
                      />
                    </div>

                    <IndexedLabel field={fields.yokomochi_pickup_date} />
                    <div>
                      <TextInput field={fields.yokomochi_pickup_date} />
                    </div>

                    <IndexedLabel field={fields.yokomochi_delivery_date} />
                    <div>
                      <TextInput field={fields.yokomochi_delivery_date} />
                    </div>
                  </>
                );
              })()}
          </IndexedFormContainer>
        </div>

        <ActionableDialogActions justifyContent="right">
          <DebugForm form={form} />
          <Button
            color="outlined"
            size="small"
            text={formStrings.cancel}
            type="button"
            onClick={onClose}
          />
          <Button type="submit" color="primary" size="small" text={strings.submit()[action]} />
        </ActionableDialogActions>
      </form>
    </>
  );
};

export interface DeliveryRequestBatchSubmitDialogProps {
  action: Exclude<DeliveryRequestBatchSubmitAction, 'BATCH_SENT'>;
  deliveryRequests: DeliveryRequestListItem[];
  open: boolean;
  onClose: () => void;
}

export const DeliveryRequestBatchSubmitDialog: React.FC<DeliveryRequestBatchSubmitDialogProps> = (
  props,
) => {
  const { action, deliveryRequests, open, onClose } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const truckCompaniesQuery = dashboardPartnerApis.useGet('/dashboard/partner');

  const batchSubmitErrorsResult = useMemo(
    () => resolveDeliveryRequestBatchSubmitErrors(deliveryRequests, action),
    [deliveryRequests, action],
  );

  const renderErrors = () => {
    if (batchSubmitErrorsResult._tag === 'Left') {
      return (
        <>
          <div
            className={clsx(classes.middleText, classes.textGray500)}
            style={{ padding: '2rem' }}
          >
            {strings.cannotBatchSubmitDeliveryRequest}

            <ul>
              {batchSubmitErrorsResult.left.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>

            <Button
              color="primary"
              style={{ width: 100, marginTop: 50 }}
              text="OK"
              size="small"
              onClick={onClose}
            />
          </div>
        </>
      );
    }
  };

  return (
    <ActionableDialog open={open} onClose={onClose} size="md">
      <QueryContainer queries={[truckCompaniesQuery]}>
        {action && batchSubmitErrorsResult._tag === 'Right' && (
          <DeliveryRequestBatchSubmitDialogContent
            {...props}
            partners={truckCompaniesQuery.data!}
          />
        )}

        {renderErrors()}
      </QueryContainer>
    </ActionableDialog>
  );
};
